import React, { useEffect } from 'react';
import { ITheme, Panel, PanelType, Stack } from '@fluentui/react';
import { SreInflightItem } from '../../../../../AGSS/models/SreHealth';
import { SreProgressState } from './useSreProgress';
import { SreProgressDetail } from './SreProgressDetail';
import { SreFact } from './SreFact';

interface ISreTriageDetailPanelProps {
    item?: SreInflightItem | null;
    identifier?: string | null;
    theme: ITheme;
    progress: SreProgressState;
    onDismiss: () => void;
}

/**
 * The same detail the inline chevron shows, in a panel with room for it.
 *
 * The inline expander keeps its place for scanning several rows at once; this is for reading one
 * closely, which is where the wide stage lists and long file paths stop fitting in a table row.
 */
export const SreTriageDetailPanel: React.FC<ISreTriageDetailPanelProps> = ({
    item,
    identifier,
    theme,
    progress,
    onDismiss,
}) => {
    const uniqueIdentifier = identifier ?? item?.uniqueIdentifier;
    const { load } = progress;

    useEffect(() => {
        if (uniqueIdentifier) {
            load(uniqueIdentifier);
        }
    }, [uniqueIdentifier, load]);

    const detail = uniqueIdentifier
        ? progress.get(uniqueIdentifier)
        : undefined;

    return (
        <Panel
            isOpen={Boolean(uniqueIdentifier)}
            onDismiss={onDismiss}
            type={PanelType.large}
            closeButtonAriaLabel="Close details"
            headerText="Orchestration details"
            isLightDismiss
        >
            {uniqueIdentifier && (
                <Stack tokens={{ childrenGap: 12 }}>
                    <SreFact
                        label="Identifier"
                        value={uniqueIdentifier}
                        copyable
                    />

                    <SreProgressDetail
                        item={item ?? undefined}
                        identifier={uniqueIdentifier}
                        progress={detail}
                        theme={theme}
                        onRefresh={() => progress.load(uniqueIdentifier, true)}
                    />
                </Stack>
            )}
        </Panel>
    );
};
